<template>
  <v-container fluid class="osce-page">
    <v-alert
      v-model="alert.show"
      class="mb-4"
      closable
      :type="alert.type"
      :text="alert.text"
    />

    <v-row class="align-center mb-4">
      <v-col cols="12" md="8">
        <div class="text-overline text-primary">OSCE</div>
        <h1 class="text-h5 font-weight-bold mb-1">
          Avaliação Teórica-Cognitiva - OSCE
        </h1>
      </v-col>
      <v-col v-if="!isAluno && !isHistorico" cols="12" md="4">
        <div class="d-flex justify-md-end ga-2 mb-2">
          <v-btn
            prepend-icon="mdi-send"
            color="success"
            variant="flat"
            :loading="sendingEvaluation"
            @click="sendEvaluation"
          >
            Enviar
          </v-btn>
          <v-btn
            prepend-icon="mdi-content-save"
            color="primary"
            variant="flat"
            @click="saveDraft"
          >
            Salvar
          </v-btn>
          <v-btn prepend-icon="mdi-refresh" variant="text" @click="resetForm">
            Limpar
          </v-btn>
          <v-btn
            prepend-icon="mdi-file-pdf-box"
            color="error"
            variant="tonal"
            @click="generatePdf"
          >
            Gerar PDF
          </v-btn>
        </div>
        <v-text-field
          v-model="form.emailDestinoAvaliacao"
          type="email"
          density="compact"
          prepend-inner-icon="mdi-email-send"
          label="E-mail de destino da avaliação"
          placeholder="secretaria@humanitas.edu.br"
          hide-details
        />
      </v-col>
    </v-row>

    <v-row v-if="isAluno">
      <v-col cols="12">
        <v-card prepend-icon="mdi-clipboard-check" title="Avaliações OSCE recebidas">
          <v-card-text>
            <v-progress-linear v-if="loadingStudentEvaluations" indeterminate color="primary" class="mb-4" />
            <v-alert
              v-else-if="!studentEvaluations.length"
              type="info"
              variant="tonal"
              text="Nenhuma avaliação OSCE foi enviada para você até o momento."
            />

            <template v-else>
              <v-row dense class="mb-2">
                <v-col cols="12" md="4">
                  <v-select v-model="studentFilters.discipline" label="Disciplina" :items="disciplineOptions" clearable hide-details />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select v-model="studentFilters.professor" label="Professor" :items="professorOptions" clearable hide-details />
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="selectedEvaluationId"
                    label="Data e horário da OSCE"
                    :items="evaluationOptions"
                    item-title="display"
                    item-value="id"
                    no-data-text="Nenhuma OSCE encontrada para os filtros"
                    hide-details
                  />
                </v-col>
              </v-row>

              <v-alert
                v-if="!filteredStudentEvaluations.length"
                type="info"
                variant="tonal"
                text="Nenhuma avaliação OSCE encontrada para os filtros selecionados."
              />

              <div v-else-if="selectedStudentEvaluation" class="student-osce-report">
                <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
                  <div>
                    <div class="font-weight-bold">
                      {{ selectedStudentEvaluation.avaliacao || "OSCE" }} - {{ selectedStudentEvaluation.area_conhecimento || "Área não informada" }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ selectedStudentEvaluation.turma }} | {{ selectedStudentEvaluation.data_avaliacao || "Data não informada" }} | {{ selectedStudentEvaluation.hora || "Horário não informado" }}
                    </div>
                  </div>
                  <v-chip color="primary" variant="tonal">Somente visualização</v-chip>
                </div>
                  <v-row dense>
                    <v-col cols="12" md="3">
                      <div class="text-caption text-medium-emphasis">Bimestre</div>
                      <div>{{ selectedStudentEvaluation.bimestre || "-" }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="text-caption text-medium-emphasis">Estação</div>
                      <div>{{ selectedStudentEvaluation.estacao || "-" }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="text-caption text-medium-emphasis">Sala</div>
                      <div>{{ selectedStudentEvaluation.sala || "-" }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="text-caption text-medium-emphasis">Hora</div>
                      <div>{{ selectedStudentEvaluation.hora || "-" }}</div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="text-caption text-medium-emphasis">Professor</div>
                      <div>{{ selectedStudentEvaluation.professor_nome || "-" }}</div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="text-caption text-medium-emphasis">Avaliador</div>
                      <div>{{ selectedStudentEvaluation.nome_avaliador || "-" }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="text-caption text-medium-emphasis">Conceito</div>
                      <div>{{ selectedStudentEvaluation.conceito || "-" }}</div>
                    </v-col>
                    <v-col cols="12" md="3">
                      <div class="text-caption text-medium-emphasis">Recomendação</div>
                      <div>{{ selectedStudentEvaluation.recomendacao || "-" }}</div>
                    </v-col>
                  </v-row>

                  <v-divider class="my-4" />

                  <h2 class="text-subtitle-1 font-weight-bold mb-2">Tarefas avaliadas</h2>
                  <v-table density="compact">
                    <thead>
                      <tr>
                        <th>Tarefa</th>
                        <th>A/B</th>
                        <th>Comentários</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(item, index) in selectedStudentEvaluation.tarefas"
                        :key="`student-task-${selectedStudentEvaluation.id}-${index}`"
                      >
                        <td>{{ item.tarefa || "-" }}</td>
                        <td>{{ item.desempenho || "-" }}</td>
                        <td>{{ item.comentarios || "-" }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                  <div class="osce-totals mt-3">
                    <v-chip color="success" variant="tonal">Totais A: {{ selectedTaskTotals.A }}</v-chip>
                    <v-chip color="error" variant="tonal">Totais B: {{ selectedTaskTotals.B }}</v-chip>
                  </div>

                  <template v-if="selectedStudentEvaluation.checklist && selectedStudentEvaluation.checklist.length">
                    <h2 class="text-subtitle-1 font-weight-bold mt-4 mb-2">Checklist adicional da estação</h2>
                    <v-table density="compact">
                      <thead>
                        <tr>
                          <th>Tarefa</th>
                          <th>A/B</th>
                          <th>Comentários</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in selectedStudentEvaluation.checklist"
                          :key="`student-check-${selectedStudentEvaluation.id}-${index}`"
                        >
                          <td>{{ item.tarefa || "-" }}</td>
                          <td>{{ item.realizou || "-" }}</td>
                          <td>{{ item.comentarios || "-" }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                    <div class="osce-totals mt-3">
                      <v-chip color="success" variant="tonal">Totais A: {{ selectedChecklistTotals.A }}</v-chip>
                      <v-chip color="error" variant="tonal">Totais B: {{ selectedChecklistTotals.B }}</v-chip>
                    </div>
                  </template>

                  <v-alert
                    v-if="selectedStudentEvaluation.comentarios_finais"
                    class="mt-4"
                    type="info"
                    variant="tonal"
                    :text="selectedStudentEvaluation.comentarios_finais"
                  />
              </div>
            </template>

          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else-if="isHistorico">
      <v-col cols="12">
        <v-card prepend-icon="mdi-history" title="Histórico de relatórios OSCE">
          <v-card-text>
            <v-progress-linear v-if="loadingHistoryEvaluations" indeterminate color="primary" class="mb-4" />

            <v-row dense>
              <v-col cols="12" md="4">
                <v-select
                  v-model="historyFilters.turma"
                  label="Turma"
                  :items="historyTurmaOptions"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="historyFilters.aluno"
                  label="Nome do estudante"
                  :items="historyAlunoOptions"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="selectedHistoryEvaluationId"
                  label="Relatório"
                  :items="historyEvaluationOptions"
                  item-title="display"
                  item-value="id"
                  no-data-text="Nenhum relatório encontrado"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-alert
              v-if="!loadingHistoryEvaluations && !historyEvaluations.length"
              type="info"
              variant="tonal"
              class="mt-4"
              text="Nenhum relatório OSCE salvo até o momento."
            />

            <v-alert
              v-else-if="!loadingHistoryEvaluations && !filteredHistoryEvaluations.length"
              type="info"
              variant="tonal"
              class="mt-4"
              text="Nenhum relatório encontrado para os filtros selecionados."
            />

            <div v-if="selectedHistoryEvaluation" class="mt-4">
              <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
                <div>
                  <div class="font-weight-bold">
                    {{ selectedHistoryEvaluation.avaliacao || "OSCE" }} - {{ selectedHistoryEvaluation.area_conhecimento || "Área não informada" }}
                  </div>
                  <div class="text-caption text-medium-emphasis">
                    {{ selectedHistoryEvaluation.turma || "-" }} | {{ selectedHistoryEvaluation.nome_estudante || selectedHistoryEvaluation.aluno_nome || "-" }} | {{ selectedHistoryEvaluation.data_avaliacao || "Data não informada" }} | {{ selectedHistoryEvaluation.hora || "Horário não informado" }}
                  </div>
                </div>
                <v-btn
                  prepend-icon="mdi-file-pdf-box"
                  color="error"
                  variant="tonal"
                  @click="generatePdfFromEvaluation(selectedHistoryEvaluation)"
                >
                  Gerar PDF
                </v-btn>
              </div>

              <v-row dense>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Área de conhecimento</div>
                  <div>{{ selectedHistoryForm.areaConhecimento || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Avaliação</div>
                  <div>{{ selectedHistoryForm.avaliacao || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Bimestre</div>
                  <div>{{ selectedHistoryForm.bimestre || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Período letivo</div>
                  <div>{{ selectedHistoryForm.periodoLetivo || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Estação</div>
                  <div>{{ selectedHistoryForm.estacao || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Sala</div>
                  <div>{{ selectedHistoryForm.sala || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Data</div>
                  <div>{{ selectedHistoryForm.data || "-" }}</div>
                </v-col>
                <v-col cols="12" md="3">
                  <div class="text-caption text-medium-emphasis">Hora</div>
                  <div>{{ selectedHistoryForm.hora || "-" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Turma</div>
                  <div>{{ selectedHistoryForm.turma || "-" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Estudante</div>
                  <div>{{ selectedHistoryForm.nomeEstudante || "-" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">E-mail destino</div>
                  <div>{{ selectedHistoryForm.emailDestinoAvaliacao || "-" }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption text-medium-emphasis">Docente</div>
                  <div>{{ selectedHistoryForm.nomeDocente || "-" }}</div>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="text-caption text-medium-emphasis">Avaliador</div>
                  <div>{{ selectedHistoryForm.nomeAvaliador || "-" }}</div>
                </v-col>
              </v-row>

              <v-divider class="my-4" />

              <v-row dense>
                <v-col v-if="selectedHistoryForm.estacaoTarefa" cols="12">
                  <div class="text-caption text-medium-emphasis">Estação - Tarefa</div>
                  <div class="history-text-block">{{ selectedHistoryForm.estacaoTarefa }}</div>
                </v-col>
                <v-col v-if="selectedHistoryForm.orientacoes" cols="12">
                  <div class="text-caption text-medium-emphasis">Orientações</div>
                  <div class="history-text-block">{{ selectedHistoryForm.orientacoes }}</div>
                </v-col>
                <v-col v-if="selectedHistoryForm.orientacoesAvaliador" cols="12">
                  <div class="text-caption text-medium-emphasis">Orientações para o avaliador</div>
                  <div class="history-text-block">{{ selectedHistoryForm.orientacoesAvaliador }}</div>
                </v-col>
                <v-col v-if="selectedHistoryForm.materiais" cols="12">
                  <div class="text-caption text-medium-emphasis">Materiais</div>
                  <div class="history-text-block">{{ selectedHistoryForm.materiais }}</div>
                </v-col>
                <v-col v-if="selectedHistoryForm.orientacoesAtores" cols="12">
                  <div class="text-caption text-medium-emphasis">Orientações para os atores</div>
                  <div class="history-text-block">{{ selectedHistoryForm.orientacoesAtores }}</div>
                </v-col>
                <v-col v-if="selectedHistoryForm.exames" cols="12">
                  <div class="text-caption text-medium-emphasis">Exames</div>
                  <div class="history-text-block">{{ selectedHistoryForm.exames }}</div>
                </v-col>
              </v-row>

              <template v-if="selectedHistoryForm.tarefas && selectedHistoryForm.tarefas.length">
                <v-divider class="my-4" />
                <h2 class="text-subtitle-1 font-weight-bold mb-2">Tarefas avaliadas</h2>
                <v-table density="compact">
                  <thead>
                    <tr>
                      <th>Tarefa</th>
                      <th>A/B</th>
                      <th>Comentários</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in selectedHistoryForm.tarefas"
                      :key="`history-task-${selectedHistoryEvaluation.id}-${index}`"
                    >
                      <td>{{ item.tarefa || "-" }}</td>
                      <td>{{ item.desempenho || "-" }}</td>
                      <td>{{ item.comentarios || "-" }}</td>
                    </tr>
                  </tbody>
                </v-table>
                <div class="osce-totals mt-3">
                  <v-chip color="success" variant="tonal">Totais A: {{ selectedHistoryTaskTotals.A }}</v-chip>
                  <v-chip color="error" variant="tonal">Totais B: {{ selectedHistoryTaskTotals.B }}</v-chip>
                </div>
              </template>

              <v-divider class="my-4" />

              <h2 class="text-subtitle-1 font-weight-bold mb-2">Checklist adicional da esta&ccedil;&atilde;o</h2>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>Tarefa</th>
                    <th>A/B</th>
                    <th>Comentários</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(item, index) in selectedHistoryForm.checklist || []"
                    :key="`history-check-${selectedHistoryEvaluation.id}-${index}`"
                  >
                    <td>{{ item.tarefa || "-" }}</td>
                    <td>{{ item.realizou || "-" }}</td>
                    <td>{{ item.comentarios || "-" }}</td>
                  </tr>
                </tbody>
              </v-table>
              <div class="osce-totals mt-3">
                <v-chip color="success" variant="tonal">Totais A: {{ selectedHistoryChecklistTotals.A }}</v-chip>
                <v-chip color="error" variant="tonal">Totais B: {{ selectedHistoryChecklistTotals.B }}</v-chip>
              </div>

              <v-divider class="my-4" />

              <h2 class="text-subtitle-1 font-weight-bold mb-2">Conceito e recomendação</h2>
              <v-row dense>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Conceito do avaliador</div>
                  <div>{{ selectedHistoryForm.conceito || "-" }}</div>
                </v-col>
                <v-col cols="12" md="4">
                  <div class="text-caption text-medium-emphasis">Recomenda&ccedil;&atilde;o do docente</div>
                  <div>{{ selectedHistoryForm.recomendacao || "-" }}</div>
                </v-col>
                <v-col cols="12">
                  <div class="text-caption text-medium-emphasis">Comentários finais</div>
                  <div class="history-text-block">{{ selectedHistoryForm.comentariosFinais || "-" }}</div>
                </v-col>
              </v-row>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col cols="12">
        <v-card prepend-icon="mdi-clipboard-edit" title="Dados da avaliação">
          <v-card-text>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="form.areaConhecimento"
                  label="Área de conhecimento"
                  placeholder="Ex: Pediatria II"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="form.avaliacao"
                  label="Avaliação"
                  :items="['OSCE A1', 'OSCE A2', 'OSCE A3(A1)', 'OSCE A3(A2)']"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select v-model="form.bimestre" label="Bimestre" :items="['1B', '2B']" />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field v-model="form.estacao" label="Estação" />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  v-model="form.periodoLetivo"
                  label="Período letivo"
                  placeholder="2026-1"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field v-model="form.sala" label="Sala" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.data"
                  v-mask="'##/##/####'"
                  label="Data"
                  placeholder="__/__/____"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="form.hora"
                  v-mask="'##:##'"
                  label="Hora"
                  placeholder="__:__"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.turmaId"
                  label="Turma"
                  :items="turmas"
                  item-title="display"
                  item-value="id"
                  :loading="loadingTurmas"
                  clearable
                  no-data-text="Nenhuma turma cadastrada"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.alunoId"
                  label="Nome do estudante"
                  :items="alunos"
                  item-title="display"
                  item-value="aluno_id"
                  :loading="loadingAlunos"
                  :disabled="!form.turmaId"
                  clearable
                  no-data-text="Nenhum aluno encontrado"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.nomeDocente"
                  label="Nome do docente"
                  placeholder="Prof. Dr. Pedro"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.nomeAvaliador" label="Nome do avaliador" />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card
          v-if="!isOsce912"
          class="mt-4"
          prepend-icon="mdi-format-list-checks"
          title="Tarefas avaliadas"
        >
          <v-card-text>
            <v-alert type="info" variant="tonal" class="mb-4">
              <div><strong>A:</strong> desempenho completo, seguro, sistemático, sem falhas relevantes ou com pequenas falhas sem comprometer segurança ou raciocínio.</div>
              <div class="mt-1"><strong>B:</strong> múltiplas falhas técnicas ou de comunicação, risco potencial ao paciente, conduta insegura, desorganizada ou eticamente inadequada.</div>
            </v-alert>

            <div class="osce-edit-table">
              <div class="osce-edit-table__head">
                <div>Tarefa</div>
                <div>A/B</div>
                <div>Comentários</div>
              </div>
              <div
                v-for="(item, index) in form.tarefas"
                :key="`criterio-${index}`"
                class="osce-edit-table__row"
              >
                <v-textarea
                  v-model="item.tarefa"
                  auto-grow
                  rows="2"
                  hide-details
                  :label="`Tarefa ${index + 1}`"
                />
                <v-select
                  v-model="item.desempenho"
                  :items="['A', 'B']"
                  label="A/B"
                  hide-details
                  clearable
                />
                <v-textarea
                  v-model="item.comentarios"
                  auto-grow
                  rows="2"
                  hide-details
                  label="Comentários"
                />
              </div>
            </div>
            <div class="osce-totals mt-3">
              <v-chip color="success" variant="tonal">Totais A: {{ taskTotals.A }}</v-chip>
              <v-chip color="error" variant="tonal">Totais B: {{ taskTotals.B }}</v-chip>
            </div>

          </v-card-text>
        </v-card>

        <v-card
          v-else
          class="mt-4"
          prepend-icon="mdi-clipboard-text"
          title="Estação - Tarefa"
        >
          <v-card-text>
            <v-textarea
              v-model="form.estacaoTarefa"
              auto-grow
              rows="4"
              :label="'ESTA\u00c7\u00c3O - TAREFA'"
            />
            <v-textarea
              v-model="form.orientacoes"
              auto-grow
              rows="3"
              label="ORIENTAÇÕES"
            />
          </v-card-text>
        </v-card>

        <v-card
          class="mt-4"
          :prepend-icon="isOsce912 ? 'mdi-format-list-checks' : 'mdi-check-decagram'"
          :title="isOsce912 ? 'Checklist adicional da estação' : 'Conceito e recomendação'"
        >
          <v-card-text>
            <v-row v-if="!isOsce912" dense>
              <v-col cols="12">
                <v-radio-group v-model="form.conceito" label="Conceito do avaliador" inline>
                  <v-radio label="Satisfatório" value="Satisfatório" />
                  <v-radio label="Insatisfatório" value="Insatisfatório" />
                </v-radio-group>
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.comentariosFinais"
                  auto-grow
                  label="Comentários acerca do desempenho final do aluno"
                  hint="Preenchimento obrigatório em casos insatisfatórios."
                  persistent-hint
                />
              </v-col>
              <v-col cols="12">
                <div class="osce-signature-line">
                  <div class="text-caption text-medium-emphasis">Avaliador</div>
                  <div class="font-weight-medium">{{ form.nomeAvaliador || "-" }}</div>
                </div>
              </v-col>
              <v-col cols="12">
                <v-radio-group v-model="form.recomendacao" label="Recomendação do docente" inline>
                  <v-radio label="A - Satisfatório" value="A - Satisfatório" />
                  <v-radio label="B - Insatisfatório" value="B - Insatisfatório" />
                </v-radio-group>
              </v-col>
              <v-col cols="12">
                <div class="osce-signature-line">
                  <div class="text-caption text-medium-emphasis">Docente</div>
                  <div class="font-weight-medium">{{ form.nomeDocente || "-" }}</div>
                </div>
              </v-col>
            </v-row>

            <v-divider class="my-4" />

            <div class="d-flex align-center justify-space-between mb-3">
              <h2 v-if="!isOsce912" class="text-subtitle-1 font-weight-bold mb-0">
                Checklist adicional da estação
              </h2>
              <v-btn
                prepend-icon="mdi-plus"
                size="small"
                color="primary"
                variant="tonal"
                @click="addChecklistItem"
              >
                Adicionar item
              </v-btn>
            </div>

            <v-alert type="info" variant="tonal" class="mb-4">
              <div><strong>A:</strong> desempenho completo, seguro, sistemático, sem falhas relevantes ou com pequenas falhas sem comprometer segurança ou raciocínio.</div>
              <div class="mt-1"><strong>B:</strong> múltiplas falhas técnicas ou de comunicação, risco potencial ao paciente, conduta insegura, desorganizada ou eticamente inadequada.</div>
            </v-alert>

            <v-row
              v-for="(item, index) in form.checklist"
              :key="`checklist-${index}`"
              dense
              class="align-start"
            >
              <v-col cols="12" md="5">
                <v-textarea
                  v-model="item.tarefa"
                  auto-grow
                  rows="1"
                  :label="`Tarefa ${index + 1}`"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-select
                  v-model="item.realizou"
                  :items="['A', 'B']"
                  label="A/B"
                  hide-details
                  clearable
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-textarea v-model="item.comentarios" auto-grow rows="1" label="Comentários" />
              </v-col>
              <v-col cols="12" md="1" class="pt-md-5">
                <v-btn
                  icon="mdi-delete"
                  size="small"
                  color="error"
                  variant="text"
                  @click="removeChecklistItem(index)"
                />
              </v-col>
            </v-row>
            <div class="osce-totals mt-2">
              <v-chip color="success" variant="tonal">Totais A: {{ checklistTotals.A }}</v-chip>
              <v-chip color="error" variant="tonal">Totais B: {{ checklistTotals.B }}</v-chip>
            </div>

            <template v-if="isOsce912">
              <v-divider class="my-4" />

              <h2 class="text-subtitle-1 font-weight-bold mb-3">Conceito e recomendação</h2>

              <v-row dense>
                <v-col cols="12">
                  <v-radio-group v-model="form.conceito" label="Conceito do avaliador" inline>
                    <v-radio label="Satisfatório" value="Satisfatório" />
                    <v-radio label="Insatisfatório" value="Insatisfatório" />
                  </v-radio-group>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.comentariosFinais"
                    auto-grow
                    label="Comentários acerca do desempenho final do aluno"
                    hint="Preenchimento obrigatório em casos insatisfatórios."
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12">
                  <div class="osce-signature-line">
                    <div class="text-caption text-medium-emphasis">Avaliador</div>
                    <div class="font-weight-medium">{{ form.nomeAvaliador || "-" }}</div>
                  </div>
                </v-col>
                <v-col cols="12">
                  <v-radio-group v-model="form.recomendacao" label="Recomendação do docente" inline>
                    <v-radio label="A - Satisfatório" value="A - Satisfatório" />
                    <v-radio label="B - Insatisfatório" value="B - Insatisfatório" />
                  </v-radio-group>
                </v-col>
                <v-col cols="12">
                  <div class="osce-signature-line">
                    <div class="text-caption text-medium-emphasis">Docente</div>
                    <div class="font-weight-medium">{{ form.nomeDocente || "-" }}</div>
                  </div>
                </v-col>
              </v-row>
            </template>

            <v-divider class="my-4" />

            <v-row dense>
              <v-col cols="12">
                <v-textarea
                  v-model="form.orientacoesAvaliador"
                  auto-grow
                  rows="2"
                  label="ORIENTAÇÕES PARA O AVALIADOR"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.materiais"
                  auto-grow
                  rows="2"
                  label="MATERIAIS"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.orientacoesAtores"
                  auto-grow
                  rows="2"
                  label="ORIENTAÇÕES PARA OS ATORES"
                />
              </v-col>
              <v-col cols="12">
                <v-textarea
                  v-model="form.exames"
                  auto-grow
                  rows="2"
                  label="EXAMES"
                />
              </v-col>
              <v-col v-if="!isOsce912" cols="12">
                <v-textarea
                  v-model="form.estacaoTarefa"
                  auto-grow
                  rows="2"
                  :label="'ESTA\u00c7\u00c3O - TAREFA'"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import html2pdf from "html2pdf.js";
import OSCEDataService from "@/assets/js/services/OSCEDataService";
import UserDataService from "@/assets/js/services/UserDataService";

const STORAGE_KEY = "pcse_osce_form";

const defaultTasks = () => [
  {
    tarefa:
      "Postura profissional e ética (respeito, empatia, confidencialidade, apresentação adequada).",
    desempenho: "",
    comentarios: "",
  },
  {
    tarefa:
      "Comunicação clínica clara e estruturada (escuta ativa, linguagem compreensível, organização do raciocínio).",
    desempenho: "",
    comentarios: "",
  },
  {
    tarefa:
      "Técnica correta de exame físico ou procedimento (sequência lógica, execução segura, assepsia).",
    desempenho: "",
    comentarios: "",
  },
  {
    tarefa:
      "Raciocínio clínico e priorização (integra dados, formula hipóteses coerentes, decide condutas conforme gravidade).",
    desempenho: "",
    comentarios: "",
  },
  {
    tarefa:
      "Organização, segurança do paciente e conduta responsável (monitorização, checagens de segurança, prevenção de erros, reconhecimento de limites).",
    desempenho: "",
    comentarios: "",
  },
];

const emptyChecklist = () =>
  Array.from({ length: 6 }, () => ({
    tarefa: "",
    realizou: "",
    comentarios: "",
  }));

const defaultForm = () => ({
  areaConhecimento: "",
  avaliacao: "OSCE A1",
  bimestre: "1B",
  estacao: "",
  periodoLetivo: "2026-1",
  data: "",
  turmaId: null,
  turma: "",
  alunoId: null,
  alunoRa: "",
  nomeDocente: "",
  nomeEstudante: "",
  nomeAvaliador: "",
  sala: "",
  hora: "",
  emailDestinoAvaliacao: "",
  orientacoes: "",
  orientacoesAvaliador: "",
  materiais: "",
  orientacoesAtores: "",
  exames: "",
  estacaoTarefa: "",
  conceito: "",
  recomendacao: "",
  comentariosFinais: "",
  tarefas: defaultTasks(),
  checklist: emptyChecklist(),
});

export default {
  name: "OSCE",

  data: () => ({
    form: defaultForm(),
    draftForms: {
      osce48: null,
      osce912: null,
    },
    turmas: [],
    alunos: [],
    loadingTurmas: false,
    loadingAlunos: false,
    sendingEvaluation: false,
    loadingStudentEvaluations: false,
    studentEvaluations: [],
    selectedEvaluationId: null,
    loadingHistoryEvaluations: false,
    historyEvaluations: [],
    selectedHistoryEvaluationId: null,
    historyFilters: {
      turma: null,
      aluno: null,
    },
    studentFilters: {
      discipline: null,
      professor: null,
    },
    initializingForm: false,
    alert: {
      show: false,
      type: "success",
      text: "",
    },
  }),

  mounted() {
    if (this.isAluno) {
      this.loadStudentEvaluations();
      return;
    }
    if (this.isHistorico) {
      this.loadHistoryEvaluations();
      return;
    }

    this.loadDraft();
    this.loadTurmas();
  },

  computed: {
    isAluno() {
      return this.$store.state.userType === "aluno";
    },
    isOsce912() {
      return this.$route.params.oPage === "9o-12o";
    },
    isHistorico() {
      return this.$route.params.oPage === "Historico";
    },
    allowedTurmaCodes() {
      return this.isOsce912
        ? ["T9", "T10", "T11", "T12"]
        : ["T3", "T4", "T5", "T6", "T7", "T8"];
    },
    storageKey() {
      return this.storageKeyForDraftKey(this.activeDraftKey);
    },
    activeDraftKey() {
      return this.draftKeyForPage(this.$route.params.oPage);
    },
    disciplineOptions() {
      return [...new Set(this.studentEvaluations.map((item) => item.area_conhecimento).filter(Boolean))].sort();
    },
    professorOptions() {
      return [...new Set(this.studentEvaluations.map((item) => item.professor_nome).filter(Boolean))].sort();
    },
    filteredStudentEvaluations() {
      return this.studentEvaluations.filter((item) => {
        const sameDiscipline = !this.studentFilters.discipline || item.area_conhecimento === this.studentFilters.discipline;
        const sameProfessor = !this.studentFilters.professor || item.professor_nome === this.studentFilters.professor;
        return sameDiscipline && sameProfessor;
      });
    },
    evaluationOptions() {
      return this.filteredStudentEvaluations.map((item) => ({
        ...item,
        display: this.formatEvaluationOption(item),
      }));
    },
    selectedStudentEvaluation() {
      return this.filteredStudentEvaluations.find((item) => item.id === this.selectedEvaluationId) || null;
    },
    historyTurmaOptions() {
      return [...new Set(this.historyEvaluations.map((item) => item.turma).filter(Boolean))].sort();
    },
    historyAlunoOptions() {
      return [
        ...new Set(
          this.historyEvaluations
            .filter((item) => !this.historyFilters.turma || item.turma === this.historyFilters.turma)
            .map((item) => item.nome_estudante || item.aluno_nome)
            .filter(Boolean)
        ),
      ].sort();
    },
    filteredHistoryEvaluations() {
      return this.historyEvaluations.filter((item) => {
        const aluno = item.nome_estudante || item.aluno_nome;
        const sameTurma = !this.historyFilters.turma || item.turma === this.historyFilters.turma;
        const sameAluno = !this.historyFilters.aluno || aluno === this.historyFilters.aluno;
        return sameTurma && sameAluno;
      });
    },
    historyEvaluationOptions() {
      return this.filteredHistoryEvaluations.map((item) => ({
        ...item,
        display: `${item.data_avaliacao || "Data não informada"} - ${item.hora || "Horário não informado"} | ${item.avaliacao || "OSCE"} | ${item.nome_estudante || item.aluno_nome || "Aluno não informado"}`,
      }));
    },
    selectedHistoryEvaluation() {
      return this.filteredHistoryEvaluations.find((item) => item.id === this.selectedHistoryEvaluationId) || null;
    },
    selectedHistoryForm() {
      return this.selectedHistoryEvaluation ? this.formFromEvaluation(this.selectedHistoryEvaluation) : defaultForm();
    },
    selectedHistoryTaskTotals() {
      return this.countAB(this.selectedHistoryForm.tarefas || [], "desempenho");
    },
    selectedHistoryChecklistTotals() {
      return this.countAB(this.selectedHistoryForm.checklist || [], "realizou");
    },
    taskTotals() {
      return this.countAB(this.form.tarefas, "desempenho");
    },
    checklistTotals() {
      return this.countAB(this.form.checklist, "realizou");
    },
    selectedTaskTotals() {
      return this.countAB(this.selectedStudentEvaluation?.tarefas || [], "desempenho");
    },
    selectedChecklistTotals() {
      return this.countAB(this.selectedStudentEvaluation?.checklist || [], "realizou");
    },
  },

  watch: {
    "form.turmaId"(value) {
      if (this.initializingForm) return;
      this.handleTurmaChange(value);
    },
    "form.alunoId"(value) {
      if (this.initializingForm) return;
      this.handleAlunoChange(value);
    },
    "$route.params.oPage"(newPage, oldPage) {
      if (this.isAluno) return;
      this.storeCurrentDraft(this.draftKeyForPage(oldPage));
      if (this.isHistorico) {
        this.loadHistoryEvaluations();
        return;
      }
      this.alunos = [];
      this.loadDraft(this.draftKeyForPage(newPage));
      this.loadTurmas();
    },
    "studentFilters.discipline"() {
      this.syncSelectedEvaluation();
    },
    "studentFilters.professor"() {
      this.syncSelectedEvaluation();
    },
    "historyFilters.turma"() {
      this.syncSelectedHistoryEvaluation();
    },
    "historyFilters.aluno"() {
      this.syncSelectedHistoryEvaluation();
    },
  },

  methods: {
    draftKeyForPage(page) {
      if (page === "Historico") return null;
      return page === "9o-12o" ? "osce912" : "osce48";
    },
    storageKeyForDraftKey(draftKey) {
      if (!draftKey) return STORAGE_KEY;
      return draftKey === "osce912" ? `${STORAGE_KEY}_9_12` : `${STORAGE_KEY}_4_8`;
    },
    cloneForm(form) {
      return JSON.parse(JSON.stringify(form || defaultForm()));
    },
    normalizeDraftForm(rawForm) {
      const form = {
        ...defaultForm(),
        ...(rawForm || {}),
        tarefas: Array.isArray(rawForm?.tarefas) ? rawForm.tarefas : defaultTasks(),
        checklist: Array.isArray(rawForm?.checklist) ? rawForm.checklist : emptyChecklist(),
      };
      return form;
    },
    storeCurrentDraft(draftKey = this.activeDraftKey) {
      if (!draftKey || this.isHistorico) return;
      this.draftForms[draftKey] = this.cloneForm(this.form);
    },
    countAB(items, field) {
      return (items || []).reduce(
        (totals, item) => {
          const value = String(item?.[field] || "").trim().toUpperCase();
          if (value === "A") totals.A += 1;
          if (value === "B") totals.B += 1;
          return totals;
        },
        { A: 0, B: 0 }
      );
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    nl2br(value) {
      return this.escapeHtml(value).replace(/\n/g, "<br>");
    },
    pdfValue(value) {
      const text = String(value ?? "").trim();
      return text ? this.escapeHtml(text) : "-";
    },
    pdfTextBlock(label, value) {
      const text = String(value || "").trim();
      if (!text) return "";
      return `
        <section class="pdf-section">
          <h2>${this.escapeHtml(label)}</h2>
          <div class="pdf-text">${this.nl2br(text)}</div>
        </section>
      `;
    },
    pdfRows(items, resultField) {
      return (items || [])
        .map(
          (item, index) => `
            <tr>
              <td>${index + 1}</td>
              <td>${this.pdfValue(item.tarefa)}</td>
              <td>${this.pdfValue(item[resultField])}</td>
              <td>${this.pdfValue(item.comentarios)}</td>
            </tr>
          `
        )
        .join("");
    },
    buildOscePdfHtml(sourceForm = this.form, is912 = this.isOsce912) {
      const taskTotals = this.countAB(sourceForm.tarefas, "desempenho");
      const checklistTotals = this.countAB(sourceForm.checklist, "realizou");
      const taskRows = this.pdfRows(is912 ? [] : sourceForm.tarefas, "desempenho");
      const checklistRows = this.pdfRows(sourceForm.checklist, "realizou");

      return `
        <div class="osce-pdf">
          <h1>Avalia&ccedil;&atilde;o Te&oacute;rica-Cognitiva - OSCE</h1>
          <table class="pdf-meta">
            <tbody>
              <tr><th>&Aacute;rea de conhecimento</th><td>${this.pdfValue(sourceForm.areaConhecimento)}</td><th>Avalia&ccedil;&atilde;o</th><td>${this.pdfValue(sourceForm.avaliacao)}</td></tr>
              <tr><th>Bimestre</th><td>${this.pdfValue(sourceForm.bimestre)}</td><th>Per&iacute;odo letivo</th><td>${this.pdfValue(sourceForm.periodoLetivo)}</td></tr>
              <tr><th>Esta&ccedil;&atilde;o</th><td>${this.pdfValue(sourceForm.estacao)}</td><th>Turma</th><td>${this.pdfValue(sourceForm.turma)}</td></tr>
              <tr><th>Sala</th><td>${this.pdfValue(sourceForm.sala)}</td><th>Data/Hora</th><td>${this.pdfValue(sourceForm.data)} ${this.pdfValue(sourceForm.hora)}</td></tr>
              <tr><th>Estudante</th><td>${this.pdfValue(sourceForm.nomeEstudante)}</td><th>RA</th><td>${this.pdfValue(sourceForm.alunoRa)}</td></tr>
              <tr><th>Docente</th><td>${this.pdfValue(sourceForm.nomeDocente)}</td><th>Avaliador</th><td>${this.pdfValue(sourceForm.nomeAvaliador)}</td></tr>
              <tr><th>E-mail destino</th><td colspan="3">${this.pdfValue(sourceForm.emailDestinoAvaliacao)}</td></tr>
            </tbody>
          </table>

          ${this.pdfTextBlock("Esta\u00e7\u00e3o - Tarefa", sourceForm.estacaoTarefa)}
          ${this.pdfTextBlock("Orienta\u00e7\u00f5es", sourceForm.orientacoes)}
          ${this.pdfTextBlock("Orienta\u00e7\u00f5es para o avaliador", sourceForm.orientacoesAvaliador)}
          ${this.pdfTextBlock("Materiais", sourceForm.materiais)}
          ${this.pdfTextBlock("Orienta\u00e7\u00f5es para os atores", sourceForm.orientacoesAtores)}
          ${this.pdfTextBlock("Exames", sourceForm.exames)}

          ${is912 ? "" : `
            <section class="pdf-section">
              <h2>Tarefas avaliadas</h2>
              <table class="pdf-table">
                <thead><tr><th>#</th><th>Tarefa</th><th>A/B</th><th>Coment&aacute;rios</th></tr></thead>
                <tbody>${taskRows || '<tr><td colspan="4">Nenhuma tarefa preenchida.</td></tr>'}</tbody>
              </table>
              <p class="pdf-totals">Totais A: ${taskTotals.A} | Totais B: ${taskTotals.B}</p>
            </section>
          `}

          <section class="pdf-section">
            <h2>Checklist adicional da esta&ccedil;&atilde;o</h2>
            <table class="pdf-table">
              <thead><tr><th>#</th><th>Tarefa</th><th>A/B</th><th>Coment&aacute;rios</th></tr></thead>
              <tbody>${checklistRows || '<tr><td colspan="4">Nenhum item preenchido.</td></tr>'}</tbody>
            </table>
            <p class="pdf-totals">Totais A: ${checklistTotals.A} | Totais B: ${checklistTotals.B}</p>
          </section>

          <section class="pdf-section">
            <h2>Conceito e recomenda&ccedil;&atilde;o</h2>
            <p><strong>Conceito do avaliador:</strong> ${this.pdfValue(sourceForm.conceito)}</p>
            <p><strong>Coment&aacute;rios finais:</strong> ${this.nl2br(sourceForm.comentariosFinais || "-")}</p>
            <p><strong>Recomenda&ccedil;&atilde;o do docente:</strong> ${this.pdfValue(sourceForm.recomendacao)}</p>
          </section>

          <section class="pdf-signatures">
            <div>
              <span></span>
              <strong>${this.pdfValue(sourceForm.nomeDocente)}</strong>
              <small>Docente</small>
            </div>
            <div>
              <span></span>
              <strong>${this.pdfValue(sourceForm.nomeAvaliador)}</strong>
              <small>Avaliador</small>
            </div>
            <div>
              <span></span>
              <strong>${this.pdfValue(sourceForm.nomeEstudante)}</strong>
              <small>Estudante</small>
            </div>
          </section>
        </div>
      `;
    },
    savePdf(sourceForm = this.form, is912 = this.isOsce912) {
      const element = document.createElement("div");
      element.innerHTML = this.buildOscePdfHtml(sourceForm, is912);
      document.body.appendChild(element);

      const aluno = String(sourceForm.nomeEstudante || "aluno")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();

      html2pdf()
        .set({
          margin: 0.35,
          filename: `osce_${aluno || "aluno"}.pdf`,
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "a4", orientation: "portrait" },
        })
        .from(element)
        .save()
        .finally(() => {
          document.body.removeChild(element);
        });
    },
    generatePdf() {
      this.handleAlunoChange(this.form.alunoId);
      this.savePdf(this.form, this.isOsce912);
    },
    generatePdfFromEvaluation(evaluation) {
      const sourceForm = this.formFromEvaluation(evaluation);
      this.savePdf(sourceForm, !(sourceForm.tarefas || []).length);
    },
    notify(type, text) {
      this.alert = { show: true, type, text };
    },
    formatEvaluationOption(item) {
      const date = item.data_avaliacao || "Data não informada";
      const hour = item.hora || "Horário não informado";
      const discipline = item.area_conhecimento || "Disciplina não informada";
      const professor = item.professor_nome || "Professor não informado";
      return `${date} - ${hour} | ${discipline} | ${professor}`;
    },
    syncSelectedEvaluation() {
      if (!this.filteredStudentEvaluations.length) {
        this.selectedEvaluationId = null;
        return;
      }

      const stillVisible = this.filteredStudentEvaluations.some((item) => item.id === this.selectedEvaluationId);
      if (!stillVisible) {
        this.selectedEvaluationId = this.filteredStudentEvaluations[0].id;
      }
    },
    syncSelectedHistoryEvaluation() {
      if (!this.filteredHistoryEvaluations.length) {
        this.selectedHistoryEvaluationId = null;
        return;
      }

      const stillVisible = this.filteredHistoryEvaluations.some(
        (item) => item.id === this.selectedHistoryEvaluationId
      );
      if (!stillVisible) {
        this.selectedHistoryEvaluationId = this.filteredHistoryEvaluations[0].id;
      }
    },
    formFromEvaluation(evaluation) {
      const savedForm = evaluation?.formulario || {};
      return {
        ...defaultForm(),
        ...savedForm,
        areaConhecimento: savedForm.areaConhecimento ?? evaluation?.area_conhecimento ?? "",
        avaliacao: savedForm.avaliacao ?? evaluation?.avaliacao ?? "",
        bimestre: savedForm.bimestre ?? evaluation?.bimestre ?? "",
        estacao: savedForm.estacao ?? evaluation?.estacao ?? "",
        periodoLetivo: savedForm.periodoLetivo ?? evaluation?.periodo_letivo ?? "",
        data: savedForm.data ?? evaluation?.data_avaliacao ?? "",
        turma: savedForm.turma ?? evaluation?.turma ?? "",
        nomeDocente: savedForm.nomeDocente ?? evaluation?.nome_docente ?? "",
        nomeEstudante: savedForm.nomeEstudante ?? evaluation?.nome_estudante ?? evaluation?.aluno_nome ?? "",
        alunoRa: savedForm.alunoRa ?? evaluation?.aluno_ra ?? evaluation?.aluno_ra_atual ?? "",
        nomeAvaliador: savedForm.nomeAvaliador ?? evaluation?.nome_avaliador ?? "",
        sala: savedForm.sala ?? evaluation?.sala ?? "",
        hora: savedForm.hora ?? evaluation?.hora ?? "",
        conceito: savedForm.conceito ?? evaluation?.conceito ?? "",
        recomendacao: savedForm.recomendacao ?? evaluation?.recomendacao ?? "",
        comentariosFinais: savedForm.comentariosFinais ?? evaluation?.comentarios_finais ?? "",
        tarefas: Array.isArray(evaluation?.tarefas) ? evaluation.tarefas : [],
        checklist: Array.isArray(evaluation?.checklist) ? evaluation.checklist : [],
      };
    },
    loadDraft(draftKey = this.activeDraftKey) {
      const storageKey = this.storageKeyForDraftKey(draftKey);
      const memoryDraft = this.draftForms[draftKey];
      this.initializingForm = true;

      if (memoryDraft) {
        this.form = this.normalizeDraftForm(this.cloneForm(memoryDraft));
        this.$nextTick(() => {
          this.initializingForm = false;
        });
        return;
      }

      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        this.form = defaultForm();
        this.draftForms[draftKey] = this.cloneForm(this.form);
        this.$nextTick(() => {
          this.initializingForm = false;
        });
        return;
      }

      try {
        const parsed = JSON.parse(raw);
        this.form = this.normalizeDraftForm(parsed);
        this.draftForms[draftKey] = this.cloneForm(this.form);
        this.$nextTick(() => {
          this.initializingForm = false;
        });
      } catch (_error) {
        this.form = defaultForm();
        this.draftForms[draftKey] = this.cloneForm(this.form);
        localStorage.removeItem(storageKey);
        this.$nextTick(() => {
          this.initializingForm = false;
        });
      }
    },
    async loadTurmas() {
      this.loadingTurmas = true;

      try {
        const response = await UserDataService.listTurmas();
        this.turmas = (response.data || [])
          .filter((turma) => this.allowedTurmaCodes.includes(turma.codigo))
          .map((turma) => ({
            ...turma,
            display: turma.codigo,
          }));

        if (!this.form.turmaId && this.form.turma) {
          const savedTurma = this.turmas.find(
            (turma) => turma.codigo.toLowerCase() === this.form.turma.toLowerCase()
          );
          if (savedTurma) {
            this.form.turmaId = savedTurma.id;
            this.form.turma = savedTurma.codigo;
            await this.loadAlunos(savedTurma.id);
          }
        } else if (this.form.turmaId) {
          await this.loadAlunos(this.form.turmaId);
        }
        this.handleAlunoChange(this.form.alunoId);
      } catch (_error) {
        this.notify("error", "Não foi possível carregar as turmas cadastradas.");
      } finally {
        this.loadingTurmas = false;
      }
    },
    async loadHistoryEvaluations() {
      this.loadingHistoryEvaluations = true;

      try {
        const response = await OSCEDataService.listSent();
        this.historyEvaluations = response.data || [];
        this.syncSelectedHistoryEvaluation();
      } catch (_error) {
        this.historyEvaluations = [];
        this.notify("error", "Não foi possível carregar o histórico OSCE.");
      } finally {
        this.loadingHistoryEvaluations = false;
      }
    },
    async loadAlunos(turmaId) {
      if (!turmaId) {
        this.alunos = [];
        return;
      }

      this.loadingAlunos = true;

      try {
        const response = await UserDataService.listTurmaAlunos({ id: turmaId });
        this.alunos = (response.data || []).map((aluno) => ({
          ...aluno,
          display: aluno.ra ? `${aluno.nome} - RA ${aluno.ra}` : aluno.nome,
        }));
      } catch (_error) {
        this.alunos = [];
        this.notify("error", "Não foi possível carregar os alunos da turma.");
      } finally {
        this.loadingAlunos = false;
      }
    },
    async handleTurmaChange(turmaId) {
      const selectedTurma = this.turmas.find((turma) => turma.id === turmaId);
      this.form.turma = selectedTurma ? selectedTurma.codigo : "";
      this.form.alunoId = null;
      this.form.alunoRa = "";
      this.form.nomeEstudante = "";
      await this.loadAlunos(turmaId);
    },
    handleAlunoChange(alunoId) {
      const selectedAluno = this.alunos.find((aluno) => aluno.aluno_id === alunoId);
      this.form.nomeEstudante = selectedAluno ? selectedAluno.nome : "";
      this.form.alunoRa = selectedAluno ? selectedAluno.ra : "";
    },
    async sendEvaluation() {
      if (!this.form.turmaId || !this.form.alunoId) {
        this.notify("warning", "Selecione a turma e o aluno antes de enviar a avalia\u00e7\u00e3o OSCE.");
        return;
      }
      if (!String(this.form.emailDestinoAvaliacao || "").trim()) {
        this.notify("warning", "Informe o e-mail de destino da avaliação.");
        return;
      }

      this.handleAlunoChange(this.form.alunoId);
      this.sendingEvaluation = true;

      try {
        const payloadForm = this.isOsce912 ? { ...this.form, tarefas: [] } : this.form;
        await OSCEDataService.send({ form: payloadForm });
        this.storeCurrentDraft();
        localStorage.setItem(this.storageKey, JSON.stringify(this.form));
        this.notify("success", "Avaliação OSCE enviada ao e-mail informado.");
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data?.duplicate) {
          const existing = error.response.data.existing || {};
          const confirmed = window.confirm(
            `J\u00e1 existe uma avalia\u00e7\u00e3o OSCE para este aluno em ${existing.data_avaliacao || this.form.data} \u00e0s ${existing.hora || this.form.hora}. Deseja sobrescrever o relat\u00f3rio anterior?`
          );

          if (confirmed) {
            await OSCEDataService.send({ form: payloadForm, overwrite: true });
            this.storeCurrentDraft();
            localStorage.setItem(this.storageKey, JSON.stringify(this.form));
            this.notify("success", "Avaliação OSCE anterior sobrescrita e enviada ao e-mail informado.");
          } else {
            this.notify("info", "Envio cancelado. O relat\u00f3rio anterior foi mantido.");
          }
          return;
        }

        const message = error.response?.data?.error || "N\u00e3o foi poss\u00edvel enviar a avalia\u00e7\u00e3o OSCE.";
        this.notify("error", message);
      } finally {
        this.sendingEvaluation = false;
      }
    },
    async loadStudentEvaluations() {
      this.loadingStudentEvaluations = true;

      try {
        const response = await OSCEDataService.alunoMyEvaluations();
        this.studentEvaluations = response.data || [];
        this.syncSelectedEvaluation();
      } catch (_error) {
        this.notify("error", "Não foi possível carregar suas avaliações OSCE.");
      } finally {
        this.loadingStudentEvaluations = false;
      }
    },
    saveDraft() {
      this.storeCurrentDraft();
      localStorage.setItem(this.storageKey, JSON.stringify(this.form));
      this.notify("success", "Rascunho do formulário OSCE salvo neste computador.");
    },
    resetForm() {
      this.form = defaultForm();
      this.draftForms[this.activeDraftKey] = this.cloneForm(this.form);
      localStorage.removeItem(this.storageKey);
      this.notify("info", "Formulário OSCE limpo.");
    },
    addChecklistItem() {
      this.form.checklist.push({
        tarefa: "",
        realizou: "",
        comentarios: "",
      });
    },
    removeChecklistItem(index) {
      this.form.checklist.splice(index, 1);
    },
  },
};
</script>

<style scoped>
.osce-page {
  color: var(--pcse-text-main);
}

.osce-edit-table {
  display: grid;
  gap: 10px;
}

.osce-edit-table__head,
.osce-edit-table__row {
  display: grid;
  grid-template-columns: minmax(260px, 1.2fr) 116px minmax(260px, 1fr);
  gap: 10px;
  align-items: start;
}

.osce-edit-table__head {
  padding: 10px 12px;
  border-radius: 6px;
  background: #eef4f8;
  color: #172635;
  font-weight: 700;
}

.osce-edit-table__row {
  padding-bottom: 10px;
  border-bottom: 1px solid #e3eaf0;
}

.osce-totals {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.osce-signature-line {
  padding: 12px 0;
  border-bottom: 1px solid #d6e1e8;
}

.history-text-block {
  white-space: pre-wrap;
  padding: 10px 12px;
  border: 1px solid #d6e1e8;
  border-radius: 6px;
  background: #fbfdff;
}

:global(.osce-pdf) {
  width: 7.5in;
  padding: 0.15in;
  color: #172635;
  font-family: Arial, sans-serif;
  font-size: 11px;
  line-height: 1.35;
}

:global(.osce-pdf h1) {
  margin: 0 0 14px;
  font-size: 18px;
  text-align: center;
}

:global(.osce-pdf h2) {
  margin: 14px 0 8px;
  font-size: 13px;
}

:global(.pdf-meta),
:global(.pdf-table) {
  width: 100%;
  border-collapse: collapse;
}

:global(.pdf-meta th),
:global(.pdf-meta td),
:global(.pdf-table th),
:global(.pdf-table td) {
  border: 1px solid #b8c7d1;
  padding: 5px 6px;
  vertical-align: top;
}

:global(.pdf-meta th),
:global(.pdf-table th) {
  background: #eef4f8;
  font-weight: 700;
}

:global(.pdf-text) {
  min-height: 24px;
  padding: 7px;
  border: 1px solid #b8c7d1;
}

:global(.pdf-totals) {
  margin: 6px 0 0;
  text-align: right;
  font-weight: 700;
}

:global(.pdf-signatures) {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
  margin-top: 48px;
  page-break-inside: avoid;
}

:global(.pdf-signatures div) {
  text-align: center;
}

:global(.pdf-signatures span) {
  display: block;
  height: 32px;
  border-bottom: 1px solid #172635;
  margin-bottom: 7px;
}

:global(.pdf-signatures strong),
:global(.pdf-signatures small) {
  display: block;
}

@media (max-width: 900px) {
  .osce-edit-table__head {
    display: none;
  }

  .osce-edit-table__row {
    grid-template-columns: 1fr;
  }
}
</style>



